import { useEffect, useState, useRef } from "react";
import { useMotionPreferences } from "./useMotionPreferences";

/**
 * Hook to detect scroll direction
 * Returns "up" | "down" | null
 * Respects prefers-reduced-motion by always returning null (header always visible)
 */
export function useScrollDirection() {
  const [scrollDirection, setScrollDirection] = useState<"up" | "down" | null>(null);
  const lastScrollY = useRef(0);
  const ticking = useRef(false);
  const prefersReducedMotion = useMotionPreferences();

  useEffect(() => {
    // If user prefers reduced motion, don't hide header
    if (prefersReducedMotion) {
      setScrollDirection(null);
      return;
    }

    const updateScrollDirection = () => {
      const scrollY = window.scrollY;

      // Don't hide header if near top
      if (scrollY < 100) {
        setScrollDirection(null);
      } else if (scrollY > lastScrollY.current) {
        // Scrolling down
        setScrollDirection("down");
      } else if (scrollY < lastScrollY.current) {
        // Scrolling up
        setScrollDirection("up");
      }

      lastScrollY.current = scrollY > 0 ? scrollY : 0;
      ticking.current = false;
    };

    const onScroll = () => {
      if (!ticking.current) {
        window.requestAnimationFrame(updateScrollDirection);
        ticking.current = true;
      }
    };

    window.addEventListener("scroll", onScroll);

    return () => window.removeEventListener("scroll", onScroll);
  }, [prefersReducedMotion]);

  return scrollDirection;
}
